
(() => {
  const SELECTOR = '[data-sd-configurator]';

  const labelize = (value = '') =>
    String(value)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const money = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const parseDefaultSize = (value) => {
    const parts = String(value || '3x3')
      .toLowerCase()
      .split('x')
      .map((part) => Number(part.trim()));
    const width = Number.isFinite(parts[0]) ? parts[0] : 3;
    const height = Number.isFinite(parts[1]) ? parts[1] : width;
    return { width, height };
  };

  const rangeValues = (min, max, step) => {
    const values = [];
    const precision = String(step).includes('.') ? String(step).split('.')[1].length : 0;
    for (let value = min; value <= max + step / 100; value += step) {
      values.push(Number(value.toFixed(precision)));
    }
    return values;
  };

  const parseSizePresets = (presets, allowedValues) => {
    if (!Array.isArray(presets)) return [];
    const result = [];
    const seen = new Set();
    presets.forEach((preset) => {
      if (typeof preset !== 'string') return;
      const match = preset.trim().match(/^(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)$/i);
      if (!match) return;
      const width = Number(match[1]);
      const height = Number(match[2]);
      if (!allowedValues.includes(width) || !allowedValues.includes(height) || width === height) return;
      const landscapeWidth = Math.max(width, height);
      const landscapeHeight = Math.min(width, height);
      const id = `${landscapeWidth}x${landscapeHeight}`;
      if (seen.has(id)) return;
      seen.add(id);
      result.push({ id, width: landscapeWidth, height: landscapeHeight });
    });
    return result;
  };

  class StickerDistortionConfigurator {
    constructor(root) {
      this.root = root;
      this.apiBase = root.dataset.apiBase.replace(/\/$/, '');
      this.config = this.readConfig();
      this.metadata = null;
      this.abortController = null;
      this.quoteTimer = null;
      this.quoteRevision = 0;
      this.state = {
        shape: null,
        width: null,
        height: null,
        cutStyle: null,
        finish: null,
        quantity: Number(this.config.defaultQuantity || 100),
      };

      this.els = {
        loading: root.querySelector('[data-sd-loading]'),
        builder: root.querySelector('[data-sd-builder]'),
        status: root.querySelector('[data-sd-status]'),
        statusText: root.querySelector('[data-sd-status-text]'),
        shapes: root.querySelector('[data-sd-shapes]'),
        size: root.querySelector('[data-sd-size]'),
        sizeHelper: root.querySelector('[data-sd-size-helper]'),
        cutStyles: root.querySelector('[data-sd-cut-styles]'),
        finishes: root.querySelector('[data-sd-finishes]'),
        quantity: root.querySelector('[data-sd-quantity]'),
        quantityHelper: root.querySelector('[data-sd-quantity-helper]'),
        price: root.querySelector('[data-sd-price]'),
        priceLabel: root.querySelector('[data-sd-price-label]'),
        unitPrice: root.querySelector('[data-sd-unit-price]'),
        pricingVersion: root.querySelector('[data-sd-pricing-version]'),
        error: root.querySelector('[data-sd-error]'),
      };
    }

    readConfig() {
      const script = this.root.querySelector('[data-sd-product-config]');
      if (!script) return {};
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('Sticker Distortion configurator: invalid product config.', error);
        return {};
      }
    }

    async init() {
      if (!this.config.pricingKey) {
        this.fail('Pricing configuration is missing for this product.');
        return;
      }

      try {
        const url = `${this.apiBase}/metadata?pricing_key=${encodeURIComponent(this.config.pricingKey)}`;
        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok || !data.ok) throw new Error(data.message || 'Pricing metadata is unavailable.');
        this.metadata = data;
        this.initializeState();
        this.render();
        this.setStatus('Pricing connected', true);
        this.els.loading.hidden = true;
        this.els.builder.hidden = false;
        // v5.4.1: offer an explicit, verifiable restore handshake. Do not rely
        // on unacknowledged DOM events (the v5.4 restore timeout was 3 seconds).
        const applyRestore = (item) => {
          try {
            const result = this.applySavedProjectOptions(item);
            this.root.dispatchEvent(new CustomEvent('sd:cart-edit-options-applied', { bubbles: true }));
            return result;
          } catch (error) {
            this.root.dispatchEvent(new CustomEvent('sd:cart-edit-options-invalid', {
              bubbles: true, detail: { code: 'options_not_applicable' }
            }));
            throw error;
          }
        };
        this.root.sdApplySavedCartOptions = applyRestore;
        this.root.addEventListener('sd:cart-edit-set-options', event => {
          try { applyRestore(event.detail || {}); }
          catch (error) { console.warn('Sticker Distortion cart restore rejected:', error.message); }
        });
        this.scheduleQuote(0);
      } catch (error) {
        console.error('Sticker Distortion configurator metadata error:', error);
        this.fail('Live pricing is temporarily unavailable.');
      }
    }

    // Called only for an item previously retrieved from the protected cart API.
    // Re-check against live M3 constraints, never adopt a customer-supplied price.
    applySavedProjectOptions(item) {
      if (!this.metadata || !item || item.pricing_key !== this.config.pricingKey)
        throw new Error('This saved project does not belong to this product.');
      const options = this.config.optionConfig || {};
      const constraints = this.metadata.constraints || {};
      for (const [key, values] of [
        ['shape', options.shapes || constraints.allowed_shapes],
        ['cut_style', options.cut_styles || constraints.allowed_cut_styles],
        ['finish', options.finishes || constraints.allowed_finishes]
      ]) {
        if (!Array.isArray(values) || !values.includes(item[key]))
          throw new Error('Saved ' + key.replace('_',' ') + ' is not available.');
      }
      const w=Number(item.width), h=Number(item.height), quantity=Number(item.sticker_quantity);
      const min=Number(constraints.dimension_min_inches ?? this.config.sizeBehavior?.min ?? 2);
      const max=Number(constraints.dimension_max_inches ?? this.config.sizeBehavior?.max ?? 8);
      const step=Number(constraints.dimension_increment_inches ?? this.config.sizeBehavior?.step ?? 0.5);
      const onGrid=value=>Number.isFinite(value) && value>=min && value<=max &&
        Math.abs((value-min)/step - Math.round((value-min)/step))<0.00001;
      if (!onGrid(w) || !onGrid(h) || !Number.isSafeInteger(quantity) || quantity<1)
        throw new Error('Saved dimensions or quantity are no longer valid.');
      const { shape,width,height,cutStyle,finish,quantity:oldQuantity } = this.state;
      try {
        Object.assign(this.state,{shape:item.shape,width:w,height:h,cutStyle:item.cut_style,
          finish:item.finish,quantity});
        this.render();
        if(this.state.shape!==item.shape || this.state.width!==w || this.state.height!==h ||
           this.state.quantity!==quantity || this.state.cutStyle!==item.cut_style ||
           this.state.finish!==item.finish) {
          throw new Error('Saved options are no longer offered by M3.');
        }
      }catch(error){
        Object.assign(this.state,{shape,width,height,cutStyle,finish,quantity:oldQuantity});
        this.render();
        throw error;
      }
      this.scheduleQuote(0);
      return true;
    }

    initializeState() {
      const optionConfig = this.config.optionConfig || {};
      const defaultSize = parseDefaultSize(this.config.defaultSize);
      this.state.shape = optionConfig.shapes?.[0] || this.metadata.constraints?.allowed_shapes?.[0] || 'custom';
      this.state.cutStyle = optionConfig.cut_styles?.[0] || this.metadata.constraints?.allowed_cut_styles?.[0] || 'die_cut';
      this.state.finish = optionConfig.finishes?.[0] || this.metadata.constraints?.allowed_finishes?.[0] || 'gloss';
      this.state.width = defaultSize.width;
      this.state.height = defaultSize.height;
    }

    render() {
      const optionConfig = this.config.optionConfig || {};
      this.renderChoiceGroup(this.els.shapes, optionConfig.shapes || this.metadata.constraints.allowed_shapes, 'shape');
      this.renderChoiceGroup(
        this.els.cutStyles,
        optionConfig.cut_styles || this.metadata.constraints.allowed_cut_styles,
        'cutStyle'
      );
      this.renderChoiceGroup(this.els.finishes, optionConfig.finishes || this.metadata.constraints.allowed_finishes, 'finish');
      this.renderSize();
      this.renderQuantity();
      if (this.metadata.pricing_version) this.els.pricingVersion.textContent = this.metadata.pricing_version.replace(/-/g, ' ');
    }

    renderChoiceGroup(container, values = [], stateKey) {
      container.innerHTML = '';
      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sd-configurator__choice';
        button.textContent = labelize(value);
        button.dataset.value = value;
        button.setAttribute('aria-pressed', String(this.state[stateKey] === value));
        if (this.state[stateKey] === value) button.classList.add('is-selected');
        button.addEventListener('click', () => {
          this.state[stateKey] = value;
          container.querySelectorAll('.sd-configurator__choice').forEach((item) => {
            const selected = item.dataset.value === value;
            item.classList.toggle('is-selected', selected);
            item.setAttribute('aria-pressed', String(selected));
          });
          if (stateKey === 'shape') this.renderSize();
          this.scheduleQuote();
        });
        container.appendChild(button);
      });
    }

    renderSize() {
      const constraints = this.metadata.constraints || {};
      const sizeBehavior = this.config.sizeBehavior || {};
      const shapeBehavior = sizeBehavior.shapes?.[this.state.shape] || { control: 'width_height' };
      const min = Number(constraints.dimension_min_inches ?? sizeBehavior.min ?? 2);
      const max = Number(constraints.dimension_max_inches ?? sizeBehavior.max ?? 8);
      const step = Number(constraints.dimension_increment_inches ?? sizeBehavior.step ?? 0.5);
      const values = rangeValues(min, max, step);

      this.state.width = this.closestAllowed(this.state.width, values);
      this.state.height = this.closestAllowed(this.state.height, values);
      this.els.size.innerHTML = '';

      if (shapeBehavior.control === 'presets') {
        const presets = parseSizePresets(shapeBehavior.presets, values);
        if (!presets.length) {
          this.showError('Available sizes are not configured for this shape.');
          this.els.sizeHelper.textContent = 'Please contact us for this shape.';
          return;
        }
        this.renderPresetSizes(presets, shapeBehavior.allow_rotate !== false);
        return;
      }

      this.hideError();
      if (shapeBehavior.control === 'size') {
        this.state.height = this.state.width;
        this.els.size.appendChild(this.createSelect('Size', this.state.width, values, (value) => {
          this.state.width = value;
          this.state.height = value;
          if (this.state.shape === 'custom') this.updateCustomSizeHelper();
          this.scheduleQuote();
        }));
      } else {
        this.els.size.appendChild(this.createSelect('Width', this.state.width, values, (value) => {
          this.state.width = value;
          this.scheduleQuote();
        }));
        this.els.size.appendChild(this.createSelect('Height', this.state.height, values, (value) => {
          this.state.height = value;
          this.scheduleQuote();
        }));
      }

      if (this.state.shape === 'custom') {
        this.updateCustomSizeHelper();
      } else {
        this.els.sizeHelper.textContent = `${min}–${max} in · ${step} in increments`;
      }
    }

    updateCustomSizeHelper() {
      const size = Number(this.state.width);
      this.els.sizeHelper.textContent =
        `MAXIMUM ARTWORK AREA: ${size} × ${size} IN. ` +
        'YOUR DESIGN WILL FIT PROPORTIONALLY WITHIN THIS AREA. ' +
        'FINAL CUT DIMENSIONS MAY BE SMALLER.';
    }

    renderPresetSizes(presets, allowRotate) {
      const portrait = this.state.height > this.state.width;
      const current = presets.find((preset) =>
        (preset.width === this.state.width && preset.height === this.state.height) ||
        (preset.height === this.state.width && preset.width === this.state.height)
      ) || presets[0];

      this.state.width = portrait ? current.height : current.width;
      this.state.height = portrait ? current.width : current.height;
      this.hideError();

      const wrapper = document.createElement('label');
      wrapper.className = 'sd-configurator__field';
      const label = document.createElement('span');
      label.className = 'sd-configurator__field-label';
      label.textContent = 'Size';
      const select = document.createElement('select');
      select.className = 'sd-configurator__select';
      select.setAttribute('aria-label', 'Preset sticker size');

      presets.forEach((preset) => {
        const option = document.createElement('option');
        option.value = preset.id;
        const w = portrait ? preset.height : preset.width;
        const h = portrait ? preset.width : preset.height;
        option.textContent = `${w} × ${h} in`;
        option.selected = current.id === preset.id;
        select.appendChild(option);
      });
      select.addEventListener('change', () => {
        const chosen = presets.find((preset) => preset.id === select.value);
        if (!chosen) return;
        const isPortrait = this.state.height > this.state.width;
        this.state.width = isPortrait ? chosen.height : chosen.width;
        this.state.height = isPortrait ? chosen.width : chosen.height;
        this.scheduleQuote();
      });
      wrapper.append(label, select);
      this.els.size.appendChild(wrapper);

      if (allowRotate) {
        const rotateField = document.createElement('div');
        rotateField.className = 'sd-configurator__field';
        const rotateLabel = document.createElement('span');
        rotateLabel.className = 'sd-configurator__field-label';
        rotateLabel.textContent = 'Orientation';
        const rotate = document.createElement('button');
        rotate.className = 'sd-configurator__choice';
        rotate.type = 'button';
        rotate.style.minHeight = '52px';
        rotate.textContent = portrait ? 'PORTRAIT ↕' : 'LANDSCAPE ↔';
        rotate.setAttribute('aria-label', 'Swap width and height');
        rotate.addEventListener('click', () => {
          [this.state.width, this.state.height] = [this.state.height, this.state.width];
          this.renderSize();
          this.scheduleQuote();
        });
        rotateField.append(rotateLabel, rotate);
        this.els.size.appendChild(rotateField);
      }

      this.els.sizeHelper.textContent = 'Preset sizes · rotate to switch orientation';
    }

    createSelect(label, selectedValue, values, onChange) {
      const wrapper = document.createElement('label');
      wrapper.className = 'sd-configurator__field';
      const labelSpan = document.createElement('span');
      labelSpan.className = 'sd-configurator__field-label';
      labelSpan.textContent = label;
      const select = document.createElement('select');
      select.className = 'sd-configurator__select';
      select.setAttribute('aria-label', label);
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = `${value} in`;
        option.selected = Number(value) === Number(selectedValue);
        select.appendChild(option);
      });
      select.addEventListener('change', () => onChange(Number(select.value)));
      wrapper.append(labelSpan, select);
      return wrapper;
    }

    renderQuantity() {
      const constraints = this.metadata.constraints || {};
      const allowed = constraints.allowed_quantities;
      this.els.quantity.innerHTML = '';

      if (Array.isArray(allowed) && allowed.length) {
        this.state.quantity = allowed.includes(this.state.quantity) ? this.state.quantity : allowed[0];
        const wrapper = document.createElement('label');
        wrapper.className = 'sd-configurator__field';
        const label = document.createElement('span');
        label.className = 'sd-configurator__field-label';
        label.textContent = 'Quantity';
        const select = document.createElement('select');
        select.className = 'sd-configurator__select';
        allowed.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = Number(value).toLocaleString('en-US');
          option.selected = Number(value) === Number(this.state.quantity);
          select.appendChild(option);
        });
        select.addEventListener('change', () => {
          this.state.quantity = Number(select.value);
          this.scheduleQuote();
        });
        wrapper.append(label, select);
        this.els.quantity.appendChild(wrapper);
        this.els.quantityHelper.textContent = 'Fixed Price Distortion quantities.';
        return;
      }

      const min = Number(constraints.minimum_automatic_quantity || 1);
      const maxAutomatic = Number(constraints.maximum_automatic_quantity || 20000);
      const step = Number(constraints.quantity_increment || 1);
      this.state.quantity = Math.max(min, Math.round(this.state.quantity / step) * step);

      const wrapper = document.createElement('label');
      wrapper.className = 'sd-configurator__field';
      const label = document.createElement('span');
      label.className = 'sd-configurator__field-label';
      label.textContent = 'Quantity';
      const input = document.createElement('input');
      input.className = 'sd-configurator__input';
      input.type = 'number';
      input.inputMode = 'numeric';
      input.min = String(min);
      input.step = String(step);
      input.value = String(this.state.quantity);
      input.addEventListener('input', () => {
        const value = Number(input.value);
        if (!Number.isFinite(value) || value < min) return;
        this.state.quantity = Math.round(value);
        this.scheduleQuote();
      });
      wrapper.append(label, input);
      this.els.quantity.appendChild(wrapper);
      this.els.quantityHelper.textContent = `${min.toLocaleString('en-US')}–${maxAutomatic.toLocaleString('en-US')} priced instantly · larger runs get a custom quote.`;
    }

    closestAllowed(value, values) {
      const numeric = Number(value);
      return values.reduce((best, candidate) =>
        Math.abs(candidate - numeric) < Math.abs(best - numeric) ? candidate : best
      );
    }

    scheduleQuote(delay = 220) {
      // Invalidate the last accepted quote the moment controls change, not
      // 220ms later. Prevent an obsolete price from authorizing cart actions.
      ++this.quoteRevision;
      if (this.abortController) this.abortController.abort();
      this.root.dispatchEvent(new CustomEvent('sd:quote-loading', { bubbles: true }));
      window.clearTimeout(this.quoteTimer);
      this.quoteTimer = window.setTimeout(() => this.fetchQuote(), delay);
    }

    async fetchQuote() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const activeQuoteRevision = ++this.quoteRevision;
      this.root.dispatchEvent(new CustomEvent('sd:quote-loading', { bubbles: true }));
      this.hideError();
      this.els.price.classList.add('is-loading');
      this.els.price.textContent = '…';
      this.els.unitPrice.textContent = '';

      const payload = {
        program: this.config.program,
        material: this.config.material,
        width: Number(this.state.width),
        height: Number(this.state.height),
        quantity: Number(this.state.quantity),
        shape: this.state.shape,
        cut_style: this.state.cutStyle,
        finish: this.state.finish,
      };

      try {
        const response = await fetch(`${this.apiBase}/quote`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
          signal: this.abortController.signal,
        });
        const data = await response.json();
        if (!response.ok || !data.ok) throw new Error(data.message || 'Price could not be calculated.');
        if (activeQuoteRevision !== this.quoteRevision) return;
        this.renderQuote(data);
        this.root.dispatchEvent(new CustomEvent('sd:quote-ready', {
          bubbles: true,
          detail: {
            configuration: { pricing_key: this.config.pricingKey,
              program: this.config.program, material: this.config.material,
              shape: payload.shape, width: payload.width, height: payload.height,
              quantity: payload.quantity, cut_style: payload.cut_style, finish: payload.finish },
            quote: { status: data.status, quote_required: data.quote_required,
              total_price: data.total_price, currency: data.currency,
              pricing_version: data.pricing_version }
          }
        }));
      } catch (error) {
        if (error.name === 'AbortError' || activeQuoteRevision !== this.quoteRevision) return;
        this.root.dispatchEvent(new CustomEvent('sd:quote-error', { bubbles: true }));
        console.error('Sticker Distortion configurator quote error:', error);
        this.els.price.classList.remove('is-loading');
        this.els.price.textContent = '—';
        this.showError('Price could not be calculated. Try again.');
      }
    }

    renderQuote(data) {
      this.els.price.classList.remove('is-loading');
      if (data.status === 'request_quote' || data.quote_required) {
        this.els.priceLabel.textContent = 'CUSTOM QUOTE';
        this.els.price.textContent = 'LET’S TALK';
        this.els.unitPrice.textContent = data.request_quote_message || 'This quantity needs a custom quote.';
        return;
      }

      this.els.priceLabel.textContent = 'YOUR PRICE';
      this.els.price.textContent = money(Number(data.total_price || 0), data.currency || 'USD');
      this.els.unitPrice.textContent = `${money(Number(data.unit_price || 0), data.currency || 'USD')} each · ${Number(data.quantity).toLocaleString('en-US')} pcs`;
    }

    setStatus(message, connected = false) {
      this.els.statusText.textContent = message;
      this.els.status.classList.toggle('is-connected', connected);
    }

    fail(message) {
      this.els.loading.textContent = message;
      this.setStatus('Pricing offline', false);
    }

    showError(message) {
      this.els.error.textContent = message;
      this.els.error.hidden = false;
    }

    hideError() {
      this.els.error.hidden = true;
      this.els.error.textContent = '';
    }
  }

  const boot = () => {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      if (root.dataset.sdInitialized === 'true') return;
      root.dataset.sdInitialized = 'true';
      new StickerDistortionConfigurator(root).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();